import { useMemo } from 'react';

import { NavMenu, NavMenuProps } from '@design-system/components/menu/NavMenu';
import {
  faBadgePercent,
  faChartLineUp,
  faDollarCircle,
  faEnvelope,
  faEnvelopeOpenDollar,
  faGear,
  faHome,
  faMoneyBillTransfer,
  faPhotoFilm,
  faRectangleAd,
  faSignalStream,
  faSquareList,
  faUser,
  faUsers,
  faUsersRectangle,
  faBook,
  faFire,
  faFolderGrid,
} from '@fortawesome/pro-regular-svg-icons';

import { PATHS } from '../../routes';
import { AnyObject, F0 } from '../helpers/types';
import { useCommunityNotificationsCount } from '../hooks/useCommunityNotificationsCount';
import { TagLabel } from '../pages/Marketing/Marketing.page';
import { Tooltip } from '@mui/material';
import { Typography } from '@design-system/components/typography/typography';
import { bgColorsVars } from '@design-system/vars.css';
import { Link } from 'react-router';
import { sprinkles } from '@design-system/sprinkles.css';
import { faPeopleGroup } from '@fortawesome/pro-solid-svg-icons';
import { externalLink } from './AdminMenu/helpers';
import { externalUrls } from '../pages/creator/home/mappers';
import {
  getLivestreamingEnabled,
  getDmEnabled,
  getCommunityEnabled,
} from '../common/features.helpers';
import { StrategyGuideLink } from './StrategyGuideLink';
import { ResetOnboardingButton } from './ResetOnboardingButton';
import { isEligibleForOnboarding } from '../pages/onboarding/model/onboardingEligibility';

type MenuFeatureFlags = {
  isLivestreamingEnabled: boolean;
  isDmEnabled: boolean;
  isCommunityEnabled: boolean;
};
type PathParams = { communityId: string; influencerId: number };
type MenuData = { communityNotificationsCount?: number };

const getMenuList = (
  flags: MenuFeatureFlags,
  params: PathParams,
  data: MenuData,
): NavMenuProps['list'] => [
  {
    section: 'Home',
    items: [
      {
        caption: 'Home',
        to: PATHS.HOME,
        icon: faHome,
        testId: 'MainMenuItemHome',
      },
    ],
  },
  {
    section: 'Products',
    items: [
      {
        caption: 'One Time Purchases',
        to: PATHS.PURCHASES(params.influencerId),
        icon: faDollarCircle,
        testId: 'MainMenuItemOTP',
      },
      {
        caption: <SubscriptionTitle creatorId={params.influencerId} />,
        to: PATHS.SUBSCRIPTIONS(params.influencerId),
        icon: faMoneyBillTransfer,
        testId: 'MainMenuItemSubscription',
      },
      {
        caption: (
          <span className="relative" data-testid="menu-item-offers">
            Offers
            <TagLabel variant="secondary" small>
              NEW
            </TagLabel>
          </span>
        ),
        to: PATHS.BUNDLES(params.influencerId),
        icon: faFolderGrid,
        testId: 'Bundles',
      },
    ],
  },
  {
    section: 'Content',
    items: [
      {
        caption: 'Library',
        to: PATHS.LIBRARY(params.influencerId),
        icon: faPhotoFilm,
        testId: 'MainMenuItemLibrary',
      },
      {
        caption: 'Workouts',
        to: PATHS.COLLECTIONS,
        icon: faSquareList,
        subPaths: [
          PATHS.PARTNER_WORKOUTS_DRAFTS(params.influencerId),
          PATHS.PARTNER_WORKOUTS_SCHEDULED(params.influencerId),
          PATHS.PARTNER_WORKOUTS_PUBLISHED(params.influencerId),
          PATHS.PARTNER_WORKOUTS_ARCHIVED(params.influencerId),
        ],
        testId: 'MainMenuItemWorkouts',
      },
      {
        caption: 'Extras',
        to: PATHS.EXTRAS(params.influencerId),
        icon: faFire,
        testId: 'MainMenuItemExtras',
      },
    ],
  },
  {
    section: 'Education',
    items: [
      externalLink('Handbook', externalUrls['handbook'], faBook),
      externalLink(
        'Creator Community',
        externalUrls['community'],
        faPeopleGroup,
      ),
    ],
  },
  ...(flags.isLivestreamingEnabled ||
  flags.isDmEnabled ||
  flags.isCommunityEnabled
    ? [
        {
          section: 'Social',
          items: [
            ...(flags.isCommunityEnabled
              ? [
                  {
                    caption: 'Community',
                    to: params.communityId
                      ? PATHS.COMMUNITY(params.influencerId, params.communityId)
                      : PATHS.COMMUNITY_INIT(params.influencerId),
                    icon: faUsers,
                    notificationCount: data.communityNotificationsCount,
                    testId: 'MainMenuItemCommunity',
                  },
                ]
              : []),
            ...(flags.isDmEnabled
              ? [
                  {
                    caption: 'Direct Messages',
                    to: PATHS.INBOX(),
                    icon: faEnvelope,
                    testId: 'MainMenuItemDirectMessages',
                  },
                ]
              : []),
            ...(flags.isLivestreamingEnabled
              ? [
                  {
                    caption: 'Livestream',
                    to: PATHS.LIVESTREAM,
                    icon: faSignalStream,
                    testId: 'MainMenuItemLivestream',
                  },
                ]
              : []),
          ],
        },
      ]
    : []),
  {
    section: 'Tools',
    items: [
      {
        caption: 'Audience',
        to: PATHS.CRM_CREATOR(params.influencerId, 'users'),
        icon: faUsersRectangle,
        testId: 'MainMenuItemAudience',
      },
      {
        caption: 'Analytics',
        to: PATHS.PARTNER_ANALYTICS(params.influencerId),
        icon: faChartLineUp,
        testId: 'MainMenuItemAnalytics',
      },
      {
        caption: 'Payouts',
        to: PATHS.ADMIN_PAYMENT(params.influencerId),
        icon: faEnvelopeOpenDollar,
        testId: 'MainMenuItemPayouts',
      },
      {
        caption: (
          <span className="relative">
            Marketing
            <TagLabel small variant="primary">
              beta
            </TagLabel>
          </span>
        ),
        to: PATHS.MARKETING(params.influencerId),
        icon: faRectangleAd,
        testId: 'MainMenuItemMarketing',
      },
      {
        caption: (
          <span className="relative" data-testid="menu-item-promotions">
            Promotions
            <TagLabel variant="secondary" small>
              NEW
            </TagLabel>
          </span>
        ),
        to: PATHS.PROMOTIONS(params.influencerId),
        icon: faBadgePercent,
        testId: 'MainMenuItemPromotions',
      },
    ],
  },
  {
    section: 'Account',
    items: [
      {
        caption: 'Profile',
        to: PATHS.PARTNER_EDIT(params.influencerId),
        icon: faUser,
        testId: 'MainMenuItemProfile',
      },
      {
        caption: 'Settings',
        to: PATHS.PARTNER_SETTINGS(params.influencerId),
        icon: faGear,
        testId: 'MainMenuItemSettings',
      },
    ],
  },
];

export const SubscriptionTitle = ({ creatorId }: { creatorId: number }) => (
  <Tooltip
    placement="right"
    arrow
    title={
      <div className="flex flex-col gap-6">
        <div className="flex flex-col gap-1">
          <Typography variant="body" size="large">
            Landing page builder has moved
          </Typography>
          <Typography variant="body" size="medium" className="font-normal pp-2">
            The landing page builder can now be found in the subscription tab.
          </Typography>
        </div>

        <Link to={PATHS.LANDING_PAGE(creatorId)}>
          <Typography
            variant="body"
            size="medium"
            className={sprinkles({ color: 'P-80' })}
          >
            Check it out now
          </Typography>
        </Link>
      </div>
    }
    componentsProps={{
      tooltip: {
        sx: {
          bgcolor: bgColorsVars['N-40'],
          color: 'black',
          p: '16px',
        },
      },
      arrow: {
        sx: {
          color: bgColorsVars['N-40'],
        },
      },
    }}
  >
    <div className="w-[160px]" data-testid="menu-item-subscription">
      Subscription
    </div>
  </Tooltip>
);

type CreatorMenuProps = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  influencer: AnyObject; //TODO influencer type
  onLogout: F0;
};

export const CreatorMenu = (p: CreatorMenuProps) => {
  const hasInfluencer = !!p.influencer;
  const communityId = p.influencer.communityId;
  const influencerId = p.influencer.id;
  const communityNotificationsCount =
    useCommunityNotificationsCount(communityId);
  const isLivestreamingEnabled = getLivestreamingEnabled(
    p.influencer?.features,
  );
  const isDmEnabled = getDmEnabled(p.influencer?.features);
  const isCommunityEnabled = getCommunityEnabled(p.influencer?.features);
  const isEligibleForGuide = isEligibleForOnboarding(
    p.influencer?.createdAt ?? null,
  );

  const menuList = useMemo(
    () =>
      hasInfluencer
        ? getMenuList(
            {
              isLivestreamingEnabled,
              isDmEnabled,
              isCommunityEnabled,
            },
            { communityId, influencerId },
            { communityNotificationsCount },
          )
        : [],
    [
      hasInfluencer,
      isLivestreamingEnabled,
      isDmEnabled,
      isCommunityEnabled,
      communityId,
      influencerId,
      communityNotificationsCount,
    ],
  );

  return (
    <NavMenu
      list={menuList}
      onLogout={p.onLogout}
      showProgress={false}
      topContent={
        hasInfluencer ? (
          <>
            {isEligibleForGuide && (
              <StrategyGuideLink influencerId={influencerId} />
            )}
            <ResetOnboardingButton creatorId={influencerId} />
          </>
        ) : null
      }
    />
  );
};
